import {Button, Divider, HStack, Text} from '@chakra-ui/react';
import {useHalState} from '@synako/haligator';
import {Navigable} from '@synako/halitrek';
import {useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {ItemCatalogue2Desc} from 'api-types/ffconnect-hal';
import {Unite} from 'api-types/ffconnect';
import {dateAvecSlashes, useLivraisonsPossiblesContext} from 'ff-common';
import {CartonIcon} from '@/components/svg/CartonIcon';

//TODO: Sera utilisé en mode commercial
interface BoutonDLCStockProps {
  itemNav: Navigable<ItemCatalogue2Desc>;
  itemUniteDeCommande?: Unite;
}

/**
 * Bouton "Afficher DLC et stock" affiché sous la marque dans l'ArticleBlock.
 * Au clic, charge et affiche la DLC estimée et le stock physique de l'article
 * pour la livraison sélectionnée. Le fetch est différé jusqu'au clic.
 * Réservé au mode commercial (rendu gardés par `usePeutVoirDlcStock()`),
 * donc toujours visible, indépendamment du réglage client `affichage_dlc`.
 */
export const BoutonDLCStock: React.FC<BoutonDLCStockProps> = ({
  itemNav,
  itemUniteDeCommande,
}) => {
  const {t} = useTranslation();
  // Etat pour savoir si on doit charger la disponibilité (lazy fetch au clic)
  const [loadDispo, setLoadDispo] = useState(false);
  const {livraisonSelectionnee} = useLivraisonsPossiblesContext();

  // Le fetch ne se déclenche que lorsque loadDispo passe à true (au clic du bouton)
  const dispoNav = loadDispo
    ? itemNav.follow('disponibilite_article', {
        date_de_livraison: livraisonSelectionnee.data.tolede.date_de_livraison,
      })
    : undefined;
  const dispoState = useHalState(dispoNav);

  // Tant qu'on n'a pas cliqué : on affiche le bouton
  if (!loadDispo) {
    return (
      <Button
        mb={{base: '1', md: '0'}}
        size='xs'
        fontSize='10px'
        variant='outline'
        borderColor='gray.light'
        borderRadius='6px'
        bg='white'
        color='gray.dark'
        fontWeight='500'
        leftIcon={
          <CartonIcon
            color='gray.dark'
            width='15px'
            height='15px'
          />
        }
        onClick={(e) => {
          e.stopPropagation();
          setLoadDispo(true);
        }}
      >
        {t('ligne_produit.afficher_dlc_stock')}
      </Button>
    );
  }

  // Pendant le chargement
  if (dispoState.loading || !dispoState.data) {
    return (
      <Text
        fontSize='10px'
        color='gray.dark'
      >
        {t('global.chargement')}
      </Text>
    );
  }

  const hasDlc = dispoState.data.tolede.dlc_envisagee !== undefined;
  const hasStock = dispoState.data.tolede.quantite_physique !== undefined;

  // Aucune donnée disponible
  if (!hasDlc || !hasStock) {
    return (
      <Text
        fontSize='10px'
        color='gray.dark'
        fontWeight='500'
      >
        {t('back_office.prise_de_commande.panier.dlc_stock_sans_donnees')}
      </Text>
    );
  }

  return (
    <HStack
      spacing={2}
      flexWrap='wrap'
    >
      <HStack
        background='gray.lighter'
        borderRadius='6px'
        px='2'
        py='1'
        spacing={1}
        alignItems='center'
        flexWrap='wrap'
        rowGap={1}
      >
        <HStack spacing={1}>
          <CartonIcon
            color='gray.dark'
            width='15px'
            height='15px'
          />
          <Text
            fontSize='10px'
            color='gray.dark'
            fontWeight='600'
          >
            <Trans>
              {t('ligne_produit.encart_dlc', {
                date: dateAvecSlashes(dispoState.data.tolede.dlc_envisagee),
              })}
            </Trans>
          </Text>
        </HStack>
        <Divider
          orientation='vertical'
          borderColor='gray.dark'
          h='14px'
        />
        <Text
          fontSize='10px'
          color='gray.dark'
          fontWeight='600'
        >
          {t('back_office.prise_de_commande.panier.stock', {
            count: dispoState.data.tolede.quantite_physique,
            unite: t(`unite.${itemUniteDeCommande}`, {
              count: dispoState.data.tolede.quantite_physique,
            }),
          })}
        </Text>
      </HStack>
    </HStack>
  );
};
